import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Check, X, ClipboardCheck, Pencil } from "lucide-react";
import { formatPhone } from "@/lib/utils";
import PendingMemberEditor, { GENDERS } from "@/components/PendingMemberEditor";
import { unusedPortalMember, unresolvedMemberLinks, currentMemberLabel } from "@/lib/portal-members";

const FIELD_LABELS = {
  first_name: "First Name",
  last_name: "Last Name",
  phone: "Phone",
  email: "Email",
  address: "Address",
  dietary_restrictions: "Dietary Restrictions",
};

const MEMBER_ATTR_LABELS = {
  relationship: "Relationship",
  gender: "Gender",
  birth_year: "Birth year",
};

function memberKey(m) {
  return [
    (m.first_name || "").trim().toLowerCase(),
    (m.last_name || "").trim().toLowerCase(),
    String(m.birth_year || ""),
  ].join("|");
}

function memberLabel(m) {
  const name = [m.first_name, m.last_name].filter(Boolean).join(" ") || "Unnamed";
  const parts = [name];
  if (m.birth_year) {
    const age = new Date().getFullYear() - m.birth_year;
    parts.push(`age ~${age}`);
  }
  if (m.relationship) parts.push(m.relationship);
  if (m.gender) parts.push(m.gender);
  return parts.join(" · ");
}

function memberAttrDiffs(before, after) {
  return Object.keys(MEMBER_ATTR_LABELS).filter(
    (k) => String(before[k] ?? "") !== String(after[k] ?? "")
  );
}

function MembersDiff({ currentMembers, proposedMembers, membersSubmitted }) {
  if (!membersSubmitted) return null;

  const currentMap = new Map((currentMembers || []).map((m) => [memberKey(m), m]));
  const proposedMap = new Map((proposedMembers || []).map((m) => [memberKey(m), m]));

  const added = proposedMembers.filter((m) => !currentMap.has(memberKey(m)));
  const removed = (currentMembers || []).filter((m) => !proposedMap.has(memberKey(m)));
  const matched = proposedMembers
    .filter((m) => currentMap.has(memberKey(m)))
    .map((proposed) => ({ before: currentMap.get(memberKey(proposed)), after: proposed }));
  const changed = matched.filter(({ before, after }) => memberAttrDiffs(before, after).length > 0);
  const unchanged = matched.filter(({ before, after }) => memberAttrDiffs(before, after).length === 0);

  const hasRows = removed.length > 0 || added.length > 0 || changed.length > 0 || unchanged.length > 0;

  return (
    <div className="mt-4 space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--fb-text-muted)" }}>
        Household Members
      </div>
      <div className="overflow-x-auto rounded-lg text-xs" style={{ border: "1px solid var(--fb-border)" }}>
        <table className="w-full min-w-[360px]">
          <thead>
            <tr style={{ background: "var(--fb-bg)" }}>
              <th className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]" style={{ color: "var(--fb-text-muted)", width: "14%" }}>Change</th>
              <th className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]" style={{ color: "var(--fb-text-muted)" }}>Member</th>
              <th className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]" style={{ color: "var(--fb-text-muted)" }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {removed.map((m, i) => (
              <tr key={`rem-${i}`} className="border-t" style={{ borderColor: "var(--fb-border)", background: "#FEF2F2" }}>
                <td className="px-4 py-2 font-semibold" style={{ color: "#b91c1c" }}>Removed</td>
                <td className="px-4 py-2 font-mono" style={{ color: "#b91c1c" }}>{memberLabel(m)}</td>
                <td className="px-4 py-2" style={{ color: "#b91c1c" }}>—</td>
              </tr>
            ))}
            {added.map((m, i) => (
              <tr key={`add-${i}`} className="border-t" style={{ borderColor: "var(--fb-border)", background: "#F0FDF4" }}>
                <td className="px-4 py-2 font-semibold" style={{ color: "#15803d" }}>Added</td>
                <td className="px-4 py-2 font-mono" style={{ color: "#15803d" }}>{memberLabel(m)}</td>
                <td className="px-4 py-2" style={{ color: "#15803d" }}>—</td>
              </tr>
            ))}
            {changed.map(({ before, after }, i) => {
              const diffs = memberAttrDiffs(before, after);
              return (
                <tr key={`chg-${i}`} className="border-t" style={{ borderColor: "var(--fb-border)", background: "#FFFBEB" }}>
                  <td className="px-4 py-2 font-semibold" style={{ color: "#B45309" }}>Changed</td>
                  <td className="px-4 py-2 font-mono" style={{ color: "#92400E" }}>{memberLabel(after)}</td>
                  <td className="px-4 py-2">
                    <div className="space-y-0.5">
                      {diffs.map((k) => (
                        <div key={k} className="flex gap-1 items-baseline">
                          <span className="font-semibold" style={{ color: "#92400E" }}>{MEMBER_ATTR_LABELS[k]}:</span>
                          <span className="font-mono line-through" style={{ color: "#b91c1c" }}>{fmt(before[k])}</span>
                          <span className="font-mono" style={{ color: "#15803d" }}>→ {fmt(after[k])}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
            {unchanged.map(({ after }, i) => (
              <tr key={`unc-${i}`} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                <td className="px-4 py-2" style={{ color: "var(--fb-text-muted)" }}>
                  {after.id && unresolvedMemberLinks(proposedMembers, currentMembers).some(index => proposedMembers[index] === after)
                    ? "Details unchanged; record link needs review" : "Unchanged"}
                </td>
                <td className="px-4 py-2 font-mono" style={{ color: "var(--fb-text-muted)" }}>{memberLabel(after)}</td>
                <td className="px-4 py-2" style={{ color: "var(--fb-text-muted)" }}>—</td>
              </tr>
            ))}
            {!hasRows && (
              <tr className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                <td colSpan={3} className="px-4 py-2 text-center" style={{ color: "var(--fb-text-muted)" }}>
                  No members proposed
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmt(val) {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  return String(val);
}

function fmtField(key, val) {
  if (key === "phone" && val) return formatPhone(val);
  if (Array.isArray(val)) return val.length === 0 ? "None" : val.join(", ");
  return fmt(val);
}

function DiffTable({ current, proposed }) {
  const keys = Object.keys(proposed || {});
  if (keys.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-lg text-xs" style={{ border: "1px solid var(--fb-border)" }}>
      <table className="w-full min-w-[380px]">
        <thead>
          <tr style={{ background: "var(--fb-bg)" }}>
            <th
              className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]"
              style={{ color: "var(--fb-text-muted)", width: "28%" }}
            >
              Field
            </th>
            <th
              className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]"
              style={{ color: "var(--fb-text-muted)" }}
            >
              Before
            </th>
            <th
              className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]"
              style={{ color: "var(--fb-text-muted)" }}
            >
              After
            </th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k, i) => {
            const oldVal = current?.[k];
            const newVal = proposed[k];
            const oldEmpty = oldVal === null || oldVal === undefined || oldVal === "";
            return (
              <tr key={k} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                <td className="px-4 py-2 font-medium" style={{ color: "var(--fb-text)" }}>
                  {FIELD_LABELS[k] ?? k}
                </td>
                <td
                  className="px-4 py-2 font-mono"
                  style={{ color: oldEmpty ? "var(--fb-text-muted)" : "#b91c1c" }}
                >
                  {fmtField(k, oldVal)}
                </td>
                <td className="px-4 py-2 font-mono" style={{ color: "#15803d" }}>
                  {fmtField(k, newVal)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function PendingUpdates() {
  const [updates, setUpdates] = useState([]);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [editMembers, setEditMembers] = useState(null);
  const [showGenderErrors, setShowGenderErrors] = useState(false);
  const [keepCurrentMembers, setKeepCurrentMembers] = useState(false);
  const [confirmedRemovals, setConfirmedRemovals] = useState(false);
  const editing = updates.find(u => u.id === editingId);
  const currentRoster = editing?.current_members || [];
  const unresolvedLinks = unresolvedMemberLinks(editMembers, currentRoster);
  const removedCurrent = currentRoster.filter(m => !(editMembers || []).some(row => row.id === m.id));

  async function load() {
    try {
      const { data } = await api.get("/updates/pending");
      setUpdates(data);
    } catch (e) { setError(formatApiError(e)); }
  }

  useEffect(() => { load(); }, []);

  async function approve(id) {
    try {
      setError("");
      await api.post(`/updates/${id}/approve`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  }

  async function reject(id) {
    await api.post(`/updates/${id}/reject`);
    await load();
  }

  function startEdit(u) {
    setKeepCurrentMembers(false);
    setConfirmedRemovals(false);
    setShowGenderErrors(false);
    setEditingId(u.id);
    setError("");
    setEditValues({ ...(u.member_review?.changes || u.proposed_changes || {}) });
    const editRoster = u.member_review ? u.member_review.replace_members :
      (u.members_submitted || (u.proposed_members || []).length);
    setEditMembers(editRoster ? [...(u.member_review?.members || u.proposed_members || [])] : null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditValues({});
    setEditMembers(null);
  }

  async function saveWithEdits() {
    setError("");
    if (!keepCurrentMembers && unresolvedLinks.length) {
      setError("Match each flagged row to the correct current household member, or choose Keep current household members.");
      document.getElementById(`pme-${unresolvedLinks[0]}-match`)?.focus();
      return;
    }
    if (!keepCurrentMembers && editMembers !== null && removedCurrent.length && !confirmedRemovals) {
      setError("Confirm the listed member removals, or keep the current household roster.");
      return;
    }
    setShowGenderErrors(true);
    const missing = keepCurrentMembers ? -1 : (editMembers || []).findIndex(m => !unusedPortalMember(m) && !GENDERS.includes(m.gender));
    if (missing >= 0) {
      setError(`Member ${missing + 1}: select a gender or Prefer not to say before approving.`);
      document.getElementById(`pme-${missing}-gender`)?.focus();
      return;
    }
    setSaving(true);
    try {
      await api.post(`/updates/${editingId}/approve`, {
        overrides: editValues,
        ...(editing?.member_review?.roster_token ? { members_review_token: editing.member_review.roster_token } : {}),
        ...(keepCurrentMembers ? { keep_current_members: true } :
          editMembers !== null ? { members_override: editMembers,
            ...(confirmedRemovals ? { confirmed_removed_member_ids: removedCurrent.map(m => m.id) } : {}) } : {}),
      });
      setEditingId(null);
      setEditValues({});
      setEditMembers(null);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="pending-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>
          Approvals
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Pending Updates</h1>
        <p className="text-sm mt-2" style={{ color: "var(--fb-text-muted)" }}>
          Changes submitted by clients via the portal. Approve to apply, reject to discard.
        </p>
      </div>

      {error && <div id="pending-approval-error" role="alert" className="text-sm fb-status-error p-3 rounded-lg">{error}</div>}

      {updates.length === 0 && (
        <div className="fb-card p-12 text-center">
          <ClipboardCheck className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--fb-text-muted)" }} />
          <div className="text-lg font-semibold mb-1">All caught up</div>
          <div className="text-sm" style={{ color: "var(--fb-text-muted)" }}>No pending updates from clients right now.</div>
        </div>
      )}

      <div className="space-y-4">
        {updates.map((u) => {
          const isEditing = editingId === u.id;
          const proposedKeys = Object.keys(u.proposed_changes || {});

          return (
            <div key={u.id} className="fb-card p-4 sm:p-6" data-testid={`pending-${u.id}`}>
              <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
                    Submitted {new Date(u.submitted_at).toLocaleString()}
                  </div>
                  <div className="text-lg font-semibold mt-1">{u.client_name}</div>
                </div>

                {!isEditing && (
                  <div className="flex gap-2 flex-wrap">
                    <button data-testid={`reject-${u.id}`} onClick={() => reject(u.id)}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-semibold border hover:bg-rose-50"
                            style={{ borderColor: "var(--fb-error)", color: "var(--fb-error)" }}>
                      <X className="w-4 h-4" /> Reject
                    </button>
                    <button data-testid={`edit-${u.id}`} onClick={() => startEdit(u)}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-semibold border hover:bg-stone-50"
                            style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}>
                      <Pencil className="w-4 h-4" /> Accept with edits
                    </button>
                    <button data-testid={`approve-${u.id}`} onClick={() => approve(u.id)}
                            disabled={!!u.member_review?.errors?.length}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-semibold text-white"
                            style={{ background: "var(--fb-success)" }}>
                      <Check className="w-4 h-4" /> Approve
                    </button>
                  </div>
                )}

                {isEditing && (
                  <div className="flex gap-2 flex-wrap">
                     <button type="button" disabled={saving} title="Discard unsaved edits and reload the current household records"
                             onClick={async () => { setError(""); cancelEdit(); await load(); }}
                             className="px-4 py-2.5 rounded-lg font-semibold border"
                             style={{ borderColor: "var(--fb-border)" }}>
                       Reload review
                     </button>
                    <button data-testid={`cancel-edit-${u.id}`} onClick={cancelEdit}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-semibold border hover:bg-stone-50"
                            style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}>
                      <X className="w-4 h-4" /> Cancel
                    </button>
                    <button data-testid={`save-edits-${u.id}`} onClick={saveWithEdits}
                            disabled={saving || (!keepCurrentMembers && (unresolvedLinks.length > 0 ||
                              (editMembers !== null && removedCurrent.length > 0 && !confirmedRemovals)))}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-semibold text-white"
                            style={{ background: "var(--fb-success)" }}>
                      <Check className="w-4 h-4" /> {saving ? "Saving…" : "Apply & Approve"}
                    </button>
                  </div>
                )}
              </div>
              {!!u.member_review?.messages?.length && <div role="status" className="text-sm mb-3">
                {u.member_review.messages.map((message, i) => <p key={i}>{message}</p>)}
                {!u.member_review.replace_members && u.member_review.ignored_rows?.length > 0 &&
                  <p>Existing household members will remain unchanged.</p>}
              </div>}
              {!!u.member_review?.errors?.length && <div role="alert" className="fb-status-error p-3 rounded-lg text-sm mb-3">
                <p>{isEditing ? "Original submission issues (checked again when you apply edits):" :
                  "Some submitted details or member record links need review. Choose Accept with edits to match current members or keep the current roster."}</p>
                {u.member_review.errors.map((message, i) => <p key={i}>{message}</p>)}
              </div>}

              {/* Diff table — shown in normal review mode */}
              {!isEditing && (
                <>
                  <DiffTable current={u.current || {}} proposed={u.member_review?.changes || u.proposed_changes || {}} />
                  <MembersDiff
                    currentMembers={u.current_members || []}
                    proposedMembers={u.member_review?.members || u.proposed_members || []}
                    membersSubmitted={
                      u.member_review ? u.member_review.replace_members : u.members_submitted != null
                        ? u.members_submitted === true
                        : (u.proposed_members || []).length > 0
                    }
                  />
                </>
              )}
               {isEditing && editMembers !== null && <div className="mt-4 space-y-4">
                 <fieldset className="rounded-lg border p-4 space-y-2" style={{ borderColor: "var(--fb-border)" }}>
                   <legend className="px-1 font-semibold">How should household members be handled?</legend>
                   <label className="flex gap-2 items-start">
                     <input type="radio" name={`roster-mode-${u.id}`} checked={!keepCurrentMembers} disabled={saving}
                       onChange={() => setKeepCurrentMembers(false)} />
                     Review and apply submitted household members
                   </label>
                   <label className="flex gap-2 items-start">
                     <input type="radio" name={`roster-mode-${u.id}`} checked={keepCurrentMembers} disabled={saving}
                       onChange={() => setKeepCurrentMembers(true)} />
                     Keep current household members — approve only the profile fields below
                   </label>
                   {keepCurrentMembers && <p role="status" className="text-sm">
                     The current household roster will not be changed. Submitted member edits will not be applied.
                   </p>}
                 </fieldset>
                 <div className="rounded-lg border p-4" style={{ borderColor: "var(--fb-border)" }}>
                   <h3 className="font-semibold">Current household members</h3>
                   {currentRoster.length ? <ul className="list-disc pl-5 text-sm">
                     {currentRoster.map(m => <li key={m.id}>{currentMemberLabel(m)}</li>)}
                   </ul> : <p>No current household members.</p>}
                 </div>
                 {!keepCurrentMembers && <>
                   {unresolvedLinks.length > 0 && <p role="status" className="text-sm">
                     {unresolvedLinks.length} submitted member {unresolvedLinks.length === 1 ? "link needs" : "links need"} review.
                     {" "}Use Current household record under each affected member. No matches are selected automatically.
                   </p>}
                   <PendingMemberEditor members={editMembers} onChange={members => {
                     setEditMembers(members); setConfirmedRemovals(false);
                   }} currentMembers={currentRoster} disabled={saving} showGenderErrors={showGenderErrors} />
                   {removedCurrent.length > 0 && <div className="rounded-lg border p-4" style={{ borderColor: "var(--fb-error)" }}>
                     <p className="font-semibold">Current members not retained by this submission</p>
                     <ul className="list-disc pl-5 text-sm">{removedCurrent.map(m => <li key={m.id}>{currentMemberLabel(m)}</li>)}</ul>
                     <p className="text-sm mt-2">Resolve missing links first. Any members still listed here will be removed if you approve the submitted roster.</p>
                     <label className="flex items-start gap-2 mt-2 text-sm">
                       <input type="checkbox" checked={confirmedRemovals} disabled={saving || unresolvedLinks.length > 0}
                         onChange={e => setConfirmedRemovals(e.target.checked)} />
                       I have reviewed and confirm removing the members listed above.
                     </label>
                   </div>}
                 </>}
               </div>}

              {/* Edit mode — current values on left, editable proposed on right */}
              {isEditing && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-xl p-4 border" style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}>
                    <div className="text-xs uppercase tracking-wider font-semibold mb-3" style={{ color: "#8B918B" }}>Current</div>
                    <dl className="space-y-2 text-sm">
                      {proposedKeys.map((k) => (
                        <div key={k} className="flex gap-3 font-semibold">
                          <dt className="w-28 shrink-0" style={{ color: "var(--fb-text-muted)" }}>{FIELD_LABELS[k] || k}</dt>
                          <dd className="flex-1 break-words font-mono text-xs" style={{ color: "#b91c1c", paddingTop: "2px" }}>
                            {fmt((u.current || {})[k])}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>

                  <div className="rounded-xl p-4 border" style={{ borderColor: "#F59E0B", background: "#FFFBEB" }}>
                    <div className="text-xs uppercase tracking-wider font-semibold mb-3" style={{ color: "#B45309" }}>
                      Edit before approving
                    </div>
                    <div className="space-y-3">
                      {proposedKeys.map((k) => (
                        <div key={k}>
                          <label className="text-xs font-semibold mb-1 block" style={{ color: "#8B918B" }}>
                            {FIELD_LABELS[k] || k}
                          </label>
                          <input
                            data-testid={`edit-field-${k}`}
                            type="text"
                            value={editValues[k] ?? ""}
                            onChange={(e) => setEditValues({ ...editValues, [k]: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border outline-none focus:border-[#052F66] text-sm"
                            style={{ borderColor: "#F59E0B", background: "#FFF" }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
